<script setup lang="ts">
import {
  IonApp,
  IonRouterOutlet,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/vue';

// some of the vue router capabilities are not in ionic router.
import {
  useApiStore,
  useCustomerStore,
  useFormStore,
  useSitterStore,
} from './store';
import { close } from 'ionicons/icons';
/* mp035 import capacitor app for hooks */
import { App as CapacitorApp } from '@capacitor/app';
import {
  registerPushNotifications,
  addPushNotificationReceivedListener,
} from './lib/push-notifications';
import MyModal from './components/MyModal.vue';

import { useAdminStore } from './store/admin';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { PluginListenerHandle } from '@capacitor/core';
import { CapacitorUpdater, BundleInfo } from '@capgo/capacitor-updater';
import { apiRequest } from './lib/api-request';
import { statusOk } from './lib/http-request';
import { CapacitorUpdaterController } from './lib/api-routes';
import { useRouter } from 'vue-router';
import { isDevelopmentVersion } from './lib/is-development-version';

// allow cypress tests to access the stores
if (window.Cypress) {
  (window as any).apiStore = useApiStore();
  (window as any).customerStore = useCustomerStore();
  (window as any).sitterStore = useSitterStore();
  (window as any).adminStore = useAdminStore();
  (window as any).formStore = useFormStore();
}

const showPushNotificationModal = ref(false);
const displayedNotification = ref<PushNotificationSchema>();
let pushNotificationsListener: PluginListenerHandle | undefined;
let appStateListener: PluginListenerHandle;
let appUrlOpenListener: PluginListenerHandle | undefined;
const apiStore = useApiStore();
const router = useRouter();

let bundleInfo: BundleInfo | undefined;

/**
 * Handle deep links from App Links (Android) and Universal Links (iOS)
 * Supports both https://app.coastalbabysitters.com.au/... and coastalbabysitters://... schemes
 */
function handleDeepLink(url: string) {
  try {
    const urlObj = new URL(url);
    let path = urlObj.pathname;

    // Remove leading slash if present
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    // If path is empty, redirect to login
    if (!path || path === '') {
      router.push({ name: 'login' });
      return;
    }

    // Try to match the path to a route
    // The router will handle the routing, including query params and hash
    router.push(urlObj.pathname + urlObj.search + urlObj.hash).catch((err) => {
      // If route doesn't exist, redirect to login
      console.warn('Deep link route not found, redirecting to login:', err);
      router.push({ name: 'login' });
    });
  } catch (error) {
    console.error('Error parsing deep link URL:', error);
    // Fallback to login page if URL parsing fails
    router.push({ name: 'login' });
  }
}

async function checkForUpdate() {
  // check for an available update
  const updateResult = await apiRequest(
    CapacitorUpdaterController.checkForUpdate,
  );
  if (
    statusOk(updateResult) &&
    updateResult.body?.data?.available &&
    updateResult.body?.data?.url &&
    updateResult.body?.data?.version &&
    !bundleInfo
  ) {
    apiStore.bundleDownloading = true;
    try {
      bundleInfo = await CapacitorUpdater.download({
        url: updateResult.body?.data?.url,
        version: updateResult.body?.data?.version,
      });
    } finally {
      apiStore.bundleDownloading = false;
    }
  }

  // if we are on the login page, then apply the update
  // immediately.
  if (bundleInfo && router.currentRoute.value.name === 'login') {
    await CapacitorUpdater.set(bundleInfo);
  }
}

onMounted(async () => {
  // call registerPushNotifications when the app first starts,
  await registerPushNotifications(true);
  pushNotificationsListener = await addPushNotificationReceivedListener(
    (notification) => {
      displayedNotification.value = notification;
      showPushNotificationModal.value = true;
    },
  );

  // hit the update available endpoint.  If it answers
  // without error, call app ready
  const updateResult = await apiRequest(
    CapacitorUpdaterController.checkForUpdate,
  );
  if (statusOk(updateResult)) {
    CapacitorUpdater.notifyAppReady();
  }

  // now run the update check on startup
  await checkForUpdate();

  // then add listeners to register push notificaitons and check
  // for updates when the app is opened.
  appStateListener = await CapacitorApp.addListener(
    'appStateChange',
    async (state) => {
      if (state.isActive) {
        // call register again everytime the app is opened to
        // update the push token
        await registerPushNotifications(false);

        await checkForUpdate();
      } else if (bundleInfo) {
        // if the app is not active, and we have a bundle info,
        // then apply the update immediately.
        await CapacitorUpdater.set(bundleInfo);
      }
    },
  );

  // Handle deep links (App Links for Android, Universal Links for iOS)
  appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', (data) => {
    handleDeepLink(data.url);
  });

  // Also check if the app was opened with a URL (for cold starts)
  const appUrl = await CapacitorApp.getLaunchUrl();
  if (appUrl?.url) {
    handleDeepLink(appUrl.url);
  }
});

onBeforeUnmount(() => {
  // technically not required, with the current app layout
  // because this component should never be unmounted,
  // but it's good practice to remove listeners
  pushNotificationsListener?.remove();
  appStateListener?.remove();
  appUrlOpenListener?.remove();
});
</script>

<template>
  <ion-app>
    <!-- overlay banner to indicate development version -->
    <div
      v-if="isDevelopmentVersion()"
      style="
        background-color: rgba(var(--ion-color-danger-rgb), 0.8);
        color: var(--ion-color-danger-contrast);
        padding: 12px 16px;
        text-align: center;
        font-weight: 500;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        pointer-events: none;
      "
    >
      Development Version
    </div>
    <!-- <router-view></router-view> -->
    <ion-router-outlet></ion-router-outlet>
    <my-modal
      v-model="showPushNotificationModal"
      style="background-color: white"
    >
      <ion-grid>
        <ion-row>
          <ion-col
            style="display: flex; align-items: center"
            class="ion-padding-start"
          >
            <h4>{{ displayedNotification?.title }}</h4>
          </ion-col>
          <ion-col size="auto" style="display: flex; align-items: center">
            <ion-button @click="showPushNotificationModal = false" fill="clear">
              <ion-icon slot="icon-only" :icon="close"></ion-icon>
            </ion-button>
          </ion-col>
        </ion-row>
        <ion-row class="ion-padding-horizontal ion-padding-bottom">
          <ion-col>
            {{ displayedNotification?.body }}
          </ion-col>
        </ion-row>
      </ion-grid>
    </my-modal>
  </ion-app>
</template>

<!-- style scoped>
ion-menu ion-content {
  --background: var(--ion-item-background, var(--ion-background-color, #fff));
}

.menu-button-class {
  --background: #fff;
  --box-shadow: none;
}

ion-menu.md ion-content {
  --padding-start: 8px;
  --padding-end: 8px;
  --padding-top: 20px;
  --padding-bottom: 20px;
}

ion-menu.md ion-list {
  padding: 20px 0;
}

ion-menu.md ion-note {
  margin-bottom: 30px;
}

ion-menu.md ion-list-header,
ion-menu.md ion-note {
  padding-left: 10px;
}

ion-menu.md ion-list#inbox-list {
  border-bottom: 1px solid var(--ion-color-step-150, #d7d8da);
}

ion-menu.md ion-list#inbox-list ion-list-header {
  font-size: 22px;
  font-weight: 600;

  min-height: 20px;
}

ion-menu.md ion-list#labels-list ion-list-header {
  font-size: 16px;

  margin-bottom: 18px;

  color: #757575;

  min-height: 26px;
}

ion-menu.md ion-item {
  --padding-start: 10px;
  --padding-end: 10px;
  border-radius: 4px;
}

ion-menu.md ion-item.selected {
  --background: rgba(var(--ion-color-primary-rgb), 0.14);
}

ion-menu.md ion-item.selected ion-icon {
  color: var(--ion-color-primary);
}

ion-menu.md ion-item ion-icon {
  color: #616e7e;
}

ion-menu.md ion-item ion-label {
  font-weight: 500;
}

ion-menu.ios ion-content {
  --padding-bottom: 20px;
}

ion-menu.ios ion-list {
  padding: 20px 0 0 0;
}

ion-menu.ios ion-note {
  line-height: 24px;
  margin-bottom: 20px;
}

ion-menu.ios ion-item {
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 50px;
}

ion-menu.ios ion-item.selected ion-icon {
  color: var(--ion-color-primary);
}

ion-menu.ios ion-item ion-icon {
  font-size: 24px;
  color: #73849a;
}

ion-menu.ios ion-list#labels-list ion-list-header {
  margin-bottom: 8px;
}

ion-menu.ios ion-list-header,
ion-menu.ios ion-note {
  padding-left: 16px;
  padding-right: 16px;
}

ion-menu.ios ion-note {
  margin-bottom: 8px;
}

ion-note {
  display: inline-block;
  font-size: 16px;

  color: var(--ion-color-medium-shade);
}

ion-item.selected {
  --color: var(--ion-color-primary);
}
</style -->
